import { createClient } from "graphql-ws";
import { ApolloClient, ApolloLink, split, InMemoryCache } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { TokenRefreshLink } from "apollo-link-token-refresh";
import { createUploadLink } from "apollo-upload-client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { localStorage, isAccessTokenExpired, fetchAccessToken, fileUploadFetch } from "dash-ui";

import { TokensModel } from "gql/generated.graphql";

const GRAPHQL_URI = process.env["REACT_APP_GRAPHQL"] as string;

const httpLink = createUploadLink({
  uri: GRAPHQL_URI,
  fetch: (uri: any, options: any): any =>
    options.useUpload ? fileUploadFetch(uri, options) : fetch(uri, options),
}) as unknown as ApolloLink;

const appKeyLink = setContext((_, context) => {
  return { headers: { app_key: process.env["REACT_APP_KEY"], ...context.headers } };
});

const authLink = setContext((_, { headers }) => {
  // Get the access token from local storage if it exists
  const accessToken = localStorage.getAccessToken();
  // Return the headers to the context so batchHttpLink can read them
  return {
    headers: {
      authorization: accessToken ? `Bearer ${accessToken}` : "",
      ...headers,
    },
  };
});

const refreshTokenLink = new TokenRefreshLink<string>({
  isTokenValidOrUndefined: () => {
    const accessToken = localStorage.getAccessToken() || "";
    return !isAccessTokenExpired(accessToken);
  },
  fetchAccessToken: (): Promise<Response> => {
    const refreshToken = localStorage.getRefreshToken() || "";
    localStorage.setAccessToken("");
    window.dispatchEvent(new Event("storage"));
    return fetchAccessToken(GRAPHQL_URI, refreshToken);
  },
  handleFetch: (accessToken: string) => {
    localStorage.setAccessToken(accessToken);
    window.dispatchEvent(new Event("storage"));
  },
  handleResponse: () => (refreshTokens: TokensModel) => ({
    access_token: refreshTokens.accessToken,
  }),
  handleError: (error: any) => {
    if (error?.message !== "Failed to fetch") {
      console.error(`Bearer token error: ${error?.message}`);
    }
  },
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${GRAPHQL_URI.replace(/^http?/, "ws")}/subscriptions`,
    connectionParams: () => {
      const accessToken = localStorage.getAccessToken();

      return {
        authorization: accessToken ? `Bearer ${accessToken}` : "",
        app_key: process.env["REACT_APP_KEY"],
      };
    },
  })
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  wsLink,
  ApolloLink.from([appKeyLink, authLink, refreshTokenLink, httpLink])
);

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: splitLink,
});
